import { useMemo, useState } from 'react';
import { admin } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  Input,
  Loading,
  Modal,
  Money,
  ProSearch,
  Row,
  Select,
  StatusBadge,
} from '@/components/ui';
import { required, str } from '@/utils/format';

const ROLE_LABELS: Record<string, string> = {
  user: 'زبون',
  distributor: 'وكيل',
  admin: 'مدير',
  support: 'دعم',
  staff: 'موظف',
};

const KYC_LABELS: Record<string, string> = {
  not_submitted: 'غير مقدَّم',
  pending: 'معلّق',
  approved: 'موثّق',
  rejected: 'مرفوض',
};

function roleLabel(role: unknown): string {
  const key = str(role, '');
  return (ROLE_LABELS[key] ?? key) || '—';
}

function kycLabel(status: unknown): string {
  const key = str(status, '');
  return (KYC_LABELS[key] ?? key) || '—';
}

function agentLabel(u: JsonMap): string {
  if (str(u.role) !== 'user') return '—';
  const name = str(u.agent_name, '');
  const phone = str(u.agent_phone, '');
  if (!name && !phone && !u.agent_user_id) return 'مستقل';
  if (name && phone) return `${name} · ${phone}`;
  return name || phone || `#${str(u.agent_user_id)}`;
}

export function UsersScreen() {
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');

  const state = useAsync(
    async () => {
      const [usersRes, memRes] = await Promise.all([admin.users({}), admin.memberships()]);
      if (!usersRes.success) return usersRes;
      return {
        success: true as const,
        users: pickList(usersRes, 'data', 'users', 'items'),
        memberships: memRes.success ? pickList(memRes, 'data', 'memberships', 'plans', 'items') : [],
      };
    },
    (p) => ({
      users: Array.isArray(p.users) ? (p.users as JsonMap[]) : pickList(p, 'users', 'data', 'items'),
      memberships: Array.isArray(p.memberships)
        ? (p.memberships as JsonMap[])
        : pickList(p, 'memberships', 'data', 'items'),
    }),
    [],
  );

  const [selected, setSelected] = useState<JsonMap | null>(null);
  const [editForm, setEditForm] = useState<JsonMap | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    phone: '',
    address: '',
    password: '',
    confirm: '',
    role: 'user',
    agent_user_id: '',
  });
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [walletKind, setWalletKind] = useState('customer');
  const [newStatus, setNewStatus] = useState('active');
  const [password, setPassword] = useState('');
  const [apiEnabled, setApiEnabled] = useState(0);
  const [accessMode, setAccessMode] = useState('direct');
  const [rateLimit, setRateLimit] = useState('60');
  const [msg, setMsg] = useState('');
  const [createMsg, setCreateMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const users = state.data?.users ?? [];
  const memberships = state.data?.memberships ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== 'all' && str(u.role) !== roleFilter) return false;
      if (statusFilter !== 'all' && str(u.status) !== statusFilter) return false;
      if (agentFilter === 'bound' && !(str(u.role) === 'user' && u.agent_user_id)) return false;
      if (agentFilter === 'unbound' && !(str(u.role) === 'user' && !u.agent_user_id)) return false;
      if (!needle) return true;
      const hay = [
        str(u.name),
        str(u.phone),
        str(u.email),
        str(u.membership_name),
        str(u.agent_name),
        str(u.agent_phone),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q, roleFilter, statusFilter, agentFilter]);

  const unitsTotal = useMemo(
    () => filtered.reduce((sum, u) => sum + Number(u.wallet_balance ?? 0), 0),
    [filtered],
  );
  const gamesTotal = useMemo(
    () => filtered.reduce((sum, u) => sum + Number(u.game_wallet_balance ?? 0), 0),
    [filtered],
  );
  const activeCount = useMemo(
    () => filtered.filter((u) => str(u.status) === 'active').length,
    [filtered],
  );

  function openUser(u: JsonMap) {
    setSelected(u);
    setEditForm({
      id: u.id,
      name: str(u.name),
      phone: str(u.phone),
      address: str(u.address, ''),
      role: str(u.role, 'user'),
      status: str(u.status, 'active'),
      membership_id: Number(u.membership_id || memberships[0]?.id || 0),
      can_execute_operations: Number(u.can_execute_operations || 0),
    });
    setNewStatus(str(u.status, 'active') === 'blocked' ? 'blocked' : 'active');
    setAdjustAmount('');
    setAdjustNote('');
    setWalletKind('customer');
    setPassword('');
    setApiEnabled(Number(u.api_enabled || 0) === 1 ? 1 : 0);
    setAccessMode(str(u.api_mode) === 'approval' ? 'approval' : 'direct');
    setRateLimit(str(u.rate_limit_per_minute, '60'));
    setMsg('');
  }

  async function saveUser() {
    if (!editForm) return;
    setBusy(true);
    const res = await admin.saveUser({
      ...editForm,
      can_execute_operations: Number(editForm.can_execute_operations) ? 1 : 0,
      membership_id: Number(editForm.membership_id) || 0,
    });
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم تحديث بيانات المستخدم.' : 'تعذر الحفظ.'));
    if (res.success) {
      setSelected(null);
      setEditForm(null);
      state.reload();
    }
  }

  const agentOptions = useMemo(() => {
    const agents = users
      .filter((u) => str(u.role) === 'distributor' && str(u.status) === 'active' && !u.deleted_at)
      .map((u) => ({
        value: String(u.id),
        label: `${str(u.name)} · ${str(u.phone)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ar'));
    return [{ value: '', label: 'بدون ربط (زبون مستقل)' }, ...agents];
  }, [users]);

  function openCreate() {
    setCreateForm({
      name: '',
      phone: '',
      address: '',
      password: '',
      confirm: '',
      role: 'user',
      agent_user_id: '',
    });
    setShowCreatePassword(false);
    setCreateMsg('');
    setCreateOpen(true);
  }

  function generateCreatePassword() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$';
    let out = '';
    const bytes = new Uint8Array(14);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < bytes.length; i++) {
      out += alphabet[bytes[i]! % alphabet.length];
    }
    setCreateForm((prev) => ({ ...prev, password: out, confirm: out }));
    setShowCreatePassword(true);
    setCreateMsg('تم توليد كلمة مرور قوية. انسخها قبل الحفظ.');
  }

  async function createUser() {
    const miss = required(createForm, ['name', 'phone', 'password']);
    if (miss) {
      setCreateMsg(miss);
      return;
    }
    const cleanPhone = createForm.phone.replace(/\D/g, '');
    if (!/^09\d{8}$/.test(cleanPhone)) {
      setCreateMsg('رقم الهاتف غير صالح. استخدم 09xxxxxxxx.');
      return;
    }
    if (createForm.password.length < 10) {
      setCreateMsg('كلمة المرور يجب أن تكون 10 أحرف على الأقل.');
      return;
    }
    if (createForm.password !== createForm.confirm) {
      setCreateMsg('تأكيد كلمة المرور غير مطابق.');
      return;
    }
    if (createForm.role === 'user' && createForm.agent_user_id) {
      const agentOk = agentOptions.some((o) => o.value === createForm.agent_user_id);
      if (!agentOk) {
        setCreateMsg('الوكيل المحدد غير صالح.');
        return;
      }
    }
    setBusy(true);
    const payload: JsonMap = {
      name: createForm.name.trim(),
      phone: cleanPhone,
      password: createForm.password,
      role: createForm.role,
    };
    if (createForm.address.trim()) {
      payload.address = createForm.address.trim();
    }
    if (createForm.role === 'user' && createForm.agent_user_id) {
      payload.agent_user_id = Number(createForm.agent_user_id);
    }
    const res = await admin.createStaff(payload);
    setBusy(false);
    setCreateMsg(res.success ? str(res.message, 'تم إنشاء المستخدم.') : errorMessage(res));
    if (res.success) {
      setCreateOpen(false);
      setMsg(str(res.message, 'تم إنشاء المستخدم.'));
      state.reload();
    }
  }

  async function walletAdjust() {
    if (!selected) return;
    const miss = required({ adjustAmount }, ['adjustAmount']);
    if (miss) {
      setMsg(miss);
      return;
    }
    const raw = Number(adjustAmount);
    if (!Number.isFinite(raw) || raw === 0) {
      setMsg('أدخل مبلغاً غير صفري.');
      return;
    }
    setBusy(true);
    const res = await admin.walletAdjust({
      user_id: selected.id,
      direction: raw > 0 ? 'credit' : 'debit',
      amount: Math.abs(raw),
      note: adjustNote,
      wallet_kind: walletKind,
    });
    setBusy(false);
    setMsg(res.success ? 'تم تعديل الرصيد.' : errorMessage(res));
    if (res.success) state.reload();
  }

  async function updateApiAccess() {
    if (!selected) return;
    if (
      apiEnabled === 0 &&
      !window.confirm(`إيقاف API عن ${str(selected.name)}؟ كل الأنظمة المربوطة بالتوكن ستتوقف فوراً.`)
    ) {
      return;
    }
    setBusy(true);
    const res = await admin.userApiAccess({
      user_id: selected.id,
      enabled: apiEnabled,
      api_enabled: apiEnabled,
      access_mode: accessMode === 'approval' ? 'approval' : 'direct',
      api_mode: accessMode === 'approval' ? 'approval' : 'direct',
      rate_limit_per_minute: Math.max(10, Math.min(600, Number(rateLimit) || 60)),
    });
    setBusy(false);
    setMsg(res.success ? 'تم تحديث صلاحية API.' : errorMessage(res));
    if (res.success) state.reload();
  }

  async function setStatus() {
    if (!selected) return;
    setBusy(true);
    const res = await admin.userStatus({ user_id: selected.id, status: newStatus });
    setBusy(false);
    setMsg(res.success ? 'تم تحديث الحالة.' : errorMessage(res));
    if (res.success) state.reload();
  }

  async function toggleStatus(u: JsonMap) {
    const next = str(u.status) === 'active' ? 'blocked' : 'active';
    const label = next === 'blocked' ? 'تعطيل' : 'تفعيل';
    if (!window.confirm(`${label} حساب ${str(u.name)}؟`)) return;
    const res = await admin.userStatus({ user_id: u.id, status: next });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  async function updatePassword() {
    if (!selected) return;
    const miss = required({ password }, ['password']);
    if (miss) {
      setMsg(miss);
      return;
    }
    setBusy(true);
    const res = await admin.userPassword({ user_id: selected.id, password, new_password: password });
    setBusy(false);
    setMsg(res.success ? 'تم تحديث كلمة المرور.' : errorMessage(res));
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="users"
      title="إدارة المستخدمين"
      subtitle="إدارة الزبائن والموزعين والموظفين وبيانات محافظهم."
      actions={
        <>
          <Button type="button" variant="secondary" onClick={() => state.reload()}>
            <Icon name="refresh" size={18} /> تحديث
          </Button>
          <Button type="button" onClick={openCreate}>
            <Icon name="users" size={18} /> إضافة مستخدم
          </Button>
        </>
      }
    >
      {msg && !selected ? <FlashMessage message={msg} tone={/تعذر|فشل|خطأ|غير/.test(msg) ? 'error' : 'ok'} /> : null}
      <MetricGrid>
        <MetricCard title="كل المستخدمين" value={String(filtered.length)} icon={<Icon name="users" size={21} />} />
        <MetricCard title="فعالين" value={String(activeCount)} icon={<Icon name="check" size={21} />} />
        <MetricCard title="أرصدة الوحدات" value={<Money value={unitsTotal} />} icon={<Icon name="wallet" size={21} />} />
        <MetricCard
          title="أرصدة الألعاب"
          value={<Money value={gamesTotal} suffix="$" />}
          icon={<Icon name="game" size={21} />}
        />
      </MetricGrid>

      <FilterCard title="تصفية المستخدمين" icon={<Icon name="settings" size={24} />}>
        <ProSearch
          value={q}
          onChange={setQ}
          placeholder="بحث بالاسم، الهاتف، البريد، العضوية، الوكيل"
        />
        <Select
          label="الدور"
          value={roleFilter}
          onChange={setRoleFilter}
          options={[
            { value: 'all', label: 'كل الأدوار' },
            { value: 'user', label: 'زبون' },
            { value: 'distributor', label: 'وكيل' },
            { value: 'support', label: 'دعم' },
            { value: 'staff', label: 'موظف' },
            { value: 'admin', label: 'مدير' },
          ]}
        />
        <Select
          label="الحالة"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'active', label: 'فعال' },
            { value: 'blocked', label: 'محظور' },
          ]}
        />
        <Select
          label="التبعية"
          value={agentFilter}
          onChange={setAgentFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'bound', label: 'تابع لوكيل' },
            { value: 'unbound', label: 'زبون مستقل' },
          ]}
        />
      </FilterCard>

      <SectionCard title={`المستخدمون (${filtered.length})`} icon={<Icon name="chart" size={24} />}>
        {filtered.length === 0 ? (
          <Empty title="لا يوجد مستخدمون مطابقون" hint="عدّل التصفية أو حدّث القائمة." />
        ) : (
          <ListTable tableClassName="users-table">
            <thead>
              <tr>
                <th>المستخدم</th>
                <th>الدور/العضوية</th>
                <th>الوكيل التابع له</th>
                <th>محفظة الوحدات</th>
                <th>محفظة الألعاب</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={str(u.id)}>
                  <td>
                    <strong>{str(u.name)}</strong>
                    <small>
                      {str(u.phone)} · {str(u.email, '-')}
                    </small>
                  </td>
                  <td>
                    <div className="cell-stack">
                      <span>{roleLabel(u.role)}</span>
                      <small>
                        {str(u.membership_name, 'بدون عضوية')}
                        {Number(u.api_enabled || 0) === 1
                          ? ` — ${str(u.api_mode) === 'approval' ? 'API مكنة تحويل' : 'API عادي'}${
                              u.rate_limit_per_minute ? ` · ${str(u.rate_limit_per_minute)}/د` : ''
                            }`
                          : ''}
                      </small>
                    </div>
                  </td>
                  <td>
                    {str(u.role) === 'user' ? (
                      u.agent_user_id ? (
                        <div className="cell-stack">
                          <strong>{str(u.agent_name)}</strong>
                          <small>{str(u.agent_phone)}</small>
                        </div>
                      ) : (
                        <span className="muted">مستقل</span>
                      )
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <Money value={u.wallet_balance ?? u.balance} />
                  </td>
                  <td>
                    <Money value={u.game_wallet_balance} suffix="$" />
                  </td>
                  <td title={kycLabel(u.kyc_status)}>
                    <StatusBadge status={u.status} />
                  </td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="icon-btn" title="تعديل" onClick={() => openUser(u)}>
                        <Icon name="edit" size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="تعديل المحفظة"
                        onClick={() => openUser(u)}
                      >
                        <Icon name="wallet" size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="تغيير كلمة المرور"
                        onClick={() => openUser(u)}
                      >
                        <Icon name="lock" size={16} />
                      </button>
                      <button
                        type="button"
                        className={`icon-btn ${str(u.status) === 'active' ? 'danger' : ''}`.trim()}
                        title={str(u.status) === 'active' ? 'تعطيل الحساب' : 'تفعيل الحساب'}
                        onClick={() => void toggleStatus(u)}
                      >
                        <Icon name={str(u.status) === 'active' ? 'close' : 'check'} size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>

      <Modal
        open={!!selected}
        title={str(selected?.name, 'إدارة مستخدم')}
        subtitle="تعديل البيانات والرصيد والحالة وصلاحية API."
        onClose={() => {
          setSelected(null);
          setEditForm(null);
        }}
      >
        {selected && editForm ? (
          <div className="user-detail">
            <div className="detail-grid">
              <Row label="الهاتف" value={str(selected.phone)} />
              <Row label="الدور" value={roleLabel(selected.role)} />
              <Row label="الوكيل" value={agentLabel(selected)} />
              <Row label="التوثيق" value={kycLabel(selected.kyc_status)} />
              <Row label="العضوية" value={str(selected.membership_name, '—')} />
              <Row label="رصيد الألعاب" value={<Money value={selected.game_wallet_balance} suffix="$" />} />
            </div>

            <Form
              onSubmit={() => {
                void saveUser();
              }}
            >
              <h3 className="section-mini-title">بيانات المستخدم</h3>
              <div className="form-grid two-columns">
                <Input
                  label="الاسم"
                  value={str(editForm.name)}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
                <Input
                  label="الهاتف"
                  value={str(editForm.phone)}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  required
                />
                <Input
                  label="العنوان"
                  value={str(editForm.address, '')}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                />
                <Select
                  label="الدور"
                  value={str(editForm.role, 'user')}
                  onChange={(v) => setEditForm({ ...editForm, role: v })}
                  options={[
                    { value: 'user', label: 'زبون' },
                    { value: 'distributor', label: 'وكيل' },
                    { value: 'support', label: 'دعم' },
                    { value: 'staff', label: 'موظف' },
                    { value: 'admin', label: 'مدير' },
                  ]}
                />
                <Select
                  label="الحالة"
                  value={str(editForm.status, 'active')}
                  onChange={(v) => setEditForm({ ...editForm, status: v })}
                  options={[
                    { value: 'active', label: 'نشط' },
                    { value: 'blocked', label: 'موقوف' },
                  ]}
                />
                <Select
                  label="العضوية"
                  value={String(Number(editForm.membership_id || 0))}
                  onChange={(v) => setEditForm({ ...editForm, membership_id: Number(v) })}
                  options={memberships.map((m) => ({
                    value: String(m.id),
                    label: str(m.name),
                  }))}
                />
                <Select
                  label="تنفيذ عمليات USSD"
                  value={String(Number(editForm.can_execute_operations || 0))}
                  onChange={(v) => setEditForm({ ...editForm, can_execute_operations: Number(v) })}
                  options={[
                    { value: '0', label: 'لا' },
                    { value: '1', label: 'نعم' },
                  ]}
                />
              </div>
              <div className="action-row wrap">
                <Button type="submit" disabled={busy}>
                  <Icon name="check" size={18} /> حفظ البيانات
                </Button>
              </div>

              <h3 className="section-mini-title">تعديل الرصيد</h3>
              <Select
                label="المحفظة"
                value={walletKind}
                onChange={setWalletKind}
                options={[
                  { value: 'customer', label: 'محفظة الوحدات (ل.س)' },
                  { value: 'games', label: 'محفظة الألعاب ($)' },
                ]}
              />
              <Input
                label="المبلغ (+ إضافة / − خصم)"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                inputMode="decimal"
              />
              <Input
                label="ملاحظة التعديل"
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
              />
              <Button type="button" disabled={busy} onClick={() => void walletAdjust()}>
                تطبيق الرصيد
              </Button>

              <h3 className="section-mini-title">Partner API</h3>
              <Select
                label="تفعيل API"
                value={String(apiEnabled)}
                onChange={(v) => setApiEnabled(Number(v) ? 1 : 0)}
                options={[
                  { value: '0', label: 'معطّل' },
                  { value: '1', label: 'مفعّل' },
                ]}
              />
              <Select
                label="نوع الربط"
                value={accessMode}
                onChange={setAccessMode}
                options={[
                  { value: 'direct', label: 'API عادي (مباشر)' },
                  { value: 'approval', label: 'API مكنة تحويل' },
                ]}
              />
              <Input
                label="الحد في الدقيقة"
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
                inputMode="numeric"
                placeholder="60"
              />
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void updateApiAccess()}>
                حفظ صلاحية API
              </Button>

              <h3 className="section-mini-title">الحالة وكلمة المرور</h3>
              <Select
                label="الحالة السريعة"
                value={newStatus}
                onChange={setNewStatus}
                options={[
                  { value: 'active', label: 'نشط' },
                  { value: 'blocked', label: 'موقوف' },
                ]}
              />
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void setStatus()}>
                تحديث الحالة
              </Button>
              <Input
                label="كلمة مرور جديدة"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => void updatePassword()}
              >
                تعيين كلمة المرور
              </Button>
              <FlashMessage message={msg} />
            </Form>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={createOpen}
        title="إضافة مستخدم"
        subtitle="أنشئ حساباً جديداً مع الدور المناسب. ربط الزبون بوكيل اختياري."
        onClose={() => setCreateOpen(false)}
      >
        <Form onSubmit={() => void createUser()}>
          <h3 className="section-mini-title">البيانات الأساسية</h3>
          <div className="form-grid two-columns">
            <Input
              label="الاسم الكامل"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="مثال: أحمد محمد"
              required
            />
            <Input
              label="رقم الهاتف"
              value={createForm.phone}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  phone: e.target.value.replace(/[^\d]/g, '').slice(0, 10),
                })
              }
              placeholder="09xxxxxxxx"
              inputMode="tel"
              required
              hint="صيغة سورية: 09 متبوعاً بـ 8 أرقام"
            />
            <Select
              label="الدور"
              value={createForm.role}
              onChange={(v) =>
                setCreateForm({
                  ...createForm,
                  role: v,
                  agent_user_id: v === 'user' ? createForm.agent_user_id : '',
                })
              }
              options={[
                { value: 'user', label: 'زبون' },
                { value: 'distributor', label: 'وكيل' },
                { value: 'support', label: 'دعم' },
                { value: 'staff', label: 'موظف' },
                { value: 'admin', label: 'مدير' },
              ]}
              hint="يحدد صلاحيات الدخول ولوحة التحكم"
            />
            <Input
              label="العنوان (اختياري)"
              value={createForm.address}
              onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
              placeholder="المدينة / المنطقة"
            />
          </div>

          {createForm.role === 'user' ? (
            <>
              <h3 className="section-mini-title">ربط بالوكيل</h3>
              <Select
                label="الوكيل المرتبط"
                value={createForm.agent_user_id}
                onChange={(v) => setCreateForm({ ...createForm, agent_user_id: v })}
                options={agentOptions}
                hint="اختياري. اتركه «بدون ربط» للزبون المستقل"
              />
            </>
          ) : null}

          <h3 className="section-mini-title">كلمة المرور</h3>
          <div className="form-grid two-columns">
            <Input
              label="كلمة المرور"
              type={showCreatePassword ? 'text' : 'password'}
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              required
              hint="10 أحرف على الأقل"
              autoComplete="new-password"
            />
            <Input
              label="تأكيد كلمة المرور"
              type={showCreatePassword ? 'text' : 'password'}
              value={createForm.confirm}
              onChange={(e) => setCreateForm({ ...createForm, confirm: e.target.value })}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="action-row wrap">
            <Button type="button" variant="secondary" onClick={generateCreatePassword}>
              توليد كلمة مرور
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCreatePassword((v) => !v)}
            >
              {showCreatePassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
            </Button>
          </div>

          <div className="action-row wrap" style={{ marginTop: '1rem' }}>
            <Button type="submit" disabled={busy}>
              <Icon name="check" size={18} /> إنشاء الحساب
            </Button>
            <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
              إلغاء
            </Button>
          </div>
          <FlashMessage message={createMsg} />
        </Form>
      </Modal>
    </ScreenFrame>
  );
}
